import { assert, CustomError } from '../errors';
import { ConditionalKeys } from '../types';
import { isNullish } from './types';

export const chunkArray = <T>(arr: T[], size: number): T[][] => {
    return Array.from({ length: Math.ceil(arr.length / size) }, (_, i) =>
        arr.slice(i * size, i * size + size)
    );
};

export const sum = <T>(
    arr: T[],
    cb: (el: T) => number | null | undefined
): number => {
    return arr.reduce((agg, el) => agg + (cb(el) || 0), 0);
};

type SorterFcn<T> = (el: T) => string | number;

export const sortBy = <T>(
    arr: T[],
    /**
     * When supplying an object key (`string`), property values
     * must be `string` or `number`, not `null` or `undefined`.
     */
    sorter: ConditionalKeys<T, string | number> | SorterFcn<T>,
    order: 'ASC' | 'DESC' = 'ASC'
): T[] => {
    let sorterFcn: SorterFcn<T>;
    if (typeof sorter !== 'function') {
        sorterFcn = (el: T) => {
            assert(
                typeof el === 'object' && el && sorter in el,
                'Must be array of objects where `sorter` is a key in order to use a string `sorter`'
            );
            return el[sorter] as string | number;
        };
    } else {
        sorterFcn = sorter;
    }

    return [...arr].sort((aEl, bEl) => {
        const a = sorterFcn(aEl);
        const b = sorterFcn(bEl);

        if (order === 'ASC') {
            return a < b ? -1 : a > b ? 1 : 0;
        } else {
            return b < a ? -1 : b > a ? 1 : 0;
        }
    });
};

export const findOne = <T>(
    arr: T[],
    predicate: (value: T, index: number, array: T[]) => boolean
): T | undefined => {
    const matches = arr.filter(predicate);
    if (matches.length === 0) return;
    if (matches.length === 1) return matches[0];
    throw new CustomError(`Expected 0 or 1 matches`, {
        arr,
        matches,
    });
};

export const findExactlyOne = <T>(
    arr: T[],
    predicate: (value: T, index: number, array: T[]) => boolean
): T => {
    const matches = arr.filter(predicate);
    if (matches.length === 1) return matches[0];
    throw new CustomError(`Expected 1 match`, {
        arr,
        matches,
    });
};

export const findMinMax = <T>(
    arr: T[],
    cb: (el: T) => number[] | number | null | undefined
): { min: number; max: number } => {
    let min = 0;
    let max = 0;

    for (const el of arr) {
        let val = cb(el);

        if (isNullish(val)) continue;
        if (!Array.isArray(val)) val = [val];

        min = Math.min(min, ...val);
        max = Math.max(max, ...val);
    }

    return { min, max };
};

export const inBatches = async <T>(
    array: T[],
    batchSize: number,
    callback: (batch: T[], batchIndex: number) => Promise<void> | void
): Promise<void> => {
    for (
        let i = 0, batchIndex = 0;
        i < array.length;
        i += batchSize, batchIndex++
    ) {
        const batch = array.slice(i, i + batchSize);
        await callback(batch, batchIndex);
    }
};

/** If value already exists in the array, remove it. Otherwise, add it. */
export const toggleArrayItem = <T>(
    arr: T[] | null,
    value: T,
    { returnNullIfEmpty }: { returnNullIfEmpty: boolean }
) => {
    if (!arr) return [value];

    const newArr = arr.includes(value)
        ? // remove from array
          arr.filter((v) => v !== value)
        : // add to array
          [...arr, value];

    if (returnNullIfEmpty && newArr.length === 0) return null;

    return newArr;
};
